import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  Box,
  Drawer,
  Stack,
  Divider,
  Typography,
  IconButton,
  Button,
  Card,
  Alert,
  Link,
  Backdrop,
  CircularProgress,
  Modal as MuiModal,
  CardContent,
  CardHeader,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import CelebrationIcon from "@mui/icons-material/Celebration";
import FeatureFeedback from "../../../../../components/FeatureFeedback";

import useAuthStore from "../../../../../stores/auth";
import useHrmEmployeeStore from "../../../../../stores/hrEmployee";
import useNavStore from "../../../../../stores/nav";

import { fetchData } from "../../../../../fetchData";
import { fetchDominioData } from "../../../../../fetchDominioData";
import { personRequiredFields } from "../../../../../utils/personRequiredFields";
import {
  MunicipioOption,
  hasMunicipioEnderecoValue,
  resolveMunicipioEnderecoCode,
} from "../../../../../utils/syncAddressMunicipio";
import dayjs from "dayjs";
import {
  hasExperienceProrrogation,
  getDeterminedContractMinEndDate,
  isDeterminedEmploymentVinculo,
  isExperienceDaysTotalValid,
} from "../../../../../utils/experienceContractDates";

const focusFieldByName: Record<string, string> = {
  codigo_municipionascimento: "municipionascimento",
  municipio_endereco: "endereco_cidade",
};

const HRManagementEmployeeExtract = () => {
  const location = useLocation();
  const data: any = useHrmEmployeeStore((state) => state.data);
  const snackbar = useNavStore((state) => state.setSnackbar);
  const setData = useHrmEmployeeStore((state) => state.setState);
  const changeDraft = useHrmEmployeeStore((state) => state.changeDraft);
  const saveDraft = useHrmEmployeeStore((state) => state.saveDraft);
  const drawersRef = useHrmEmployeeStore((state) => state.drawersRef);
  const company = useAuthStore((state) => state.company);
  const [state, setState] = useState(false);
  const [error, setError] = useState(false);
  const [backdrop, setBackdrop] = useState(false);
  const [empty, setEmpty] = useState(false);
  const [required, setRequired] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const isTaxpayer = (location.state as any)?.type === "taxpayer";
  const admissionContext = isTaxpayer
    ? "ces_fopag_admission_taxpayer"
    : "ces_fopag_admission_employee";

  useEffect(() => {
    if (!state) return;

    let isActive = true;

    const validateDraft = async () => {
      setEmpty(false);
      const isRequired: any = [];
      let draft = { ...(data.draft as Record<string, any>) };

      if (!draft.municipio_endereco && draft.endereco_cidade) {
        let municipioCode = resolveMunicipioEnderecoCode(draft);

        if (municipioCode === null) {
          try {
            const response = await fetchDominioData(
              `/empresa/${company.reference}/municipios`,
              { method: "GET" },
            );
            municipioCode = resolveMunicipioEnderecoCode(
              draft,
              response?.rows as MunicipioOption[] | undefined,
            );
          } catch (error) {
            console.error(error);
          }
        }

        if (municipioCode !== null) {
          draft = { ...draft, municipio_endereco: municipioCode };
          changeDraft({
            target: {
              name: "municipio_endereco",
              value: municipioCode,
            },
          });
          saveDraft();
        }
      }

      if (!isActive) return;

      const isExperienceContractActive = (draft: Record<string, unknown>) =>
        draft.contrato_experiencia === "1" ||
        draft.contrato_experiencia === "Possui";

      personRequiredFields.forEach((field: any) => {
        if (field.name === "municipio_endereco") {
          if (!hasMunicipioEnderecoValue(draft)) {
            isRequired.push(field);
          }
          return;
        }

        if (field.name === "cargo") {
          const hasCargo = draft[field.name];
          const hasCustomCargo = draft.cargo_customizado?.trim();
          if (!hasCargo && !hasCustomCargo) {
            isRequired.push(field);
          }
          return;
        }

        if (field.name === "contrato_experiencia") {
          const experienceActive = isExperienceContractActive(draft);
          if (!experienceActive || !field.required?.length) {
            return;
          }

          field.required.forEach((subfield: any) => {
            if (!draft[subfield.name]) {
              isRequired.push(subfield);
            }
          });

          if (
            !isExperienceDaysTotalValid(
              draft.dias_experiencia,
              draft.dias_prorrogacao,
            )
          ) {
            isRequired.push({
              name: "dias_experiencia",
              label: "Quantidade de dias de experiência",
              drawer: "admission",
            });
          }
          return;
        }

        if (field.when) {
          const parentValue = draft[field.when.field];
          if (parentValue !== field.when.equals) {
            return;
          }
        }

        const value = draft[field.name];
        if (value === undefined || value === null || value === "") {
          isRequired.push(field);
          return;
        }

        if (
          field.required?.length &&
          String(field.validation) === String(draft[field.name])
        ) {
          field.required.forEach((subfield: any) => {
            if (!draft[subfield.name]) {
              isRequired.push(subfield);
            }
          });
        }
      });

      const experienceActive =
        draft.contrato_experiencia === "1" ||
        draft.contrato_experiencia === "Possui";

      if (isDeterminedEmploymentVinculo(draft.tipo_vinculo)) {
        const minEnd = getDeterminedContractMinEndDate(draft);
        const fim = draft.fim_contrato_determinado
          ? dayjs(draft.fim_contrato_determinado)
          : null;

        if (!fim?.isValid() || (minEnd && fim.isBefore(minEnd, "day"))) {
          isRequired.push({
            name: "fim_contrato_determinado",
            label: "Fim do contrato determinado",
            drawer: "admission",
          });
        }
      }

      if (experienceActive && hasExperienceProrrogation(draft)) {
        if (!draft.motivo_prorrogacao?.toString().trim()) {
          isRequired.push({
            name: "motivo_prorrogacao",
            label: "Motivo da prorrogação do contrato de experiência",
            drawer: "admission",
          });
        }
        if (!draft.fim_prorrogacao_experiencia) {
          isRequired.push({
            name: "dias_prorrogacao",
            label: "Quantidade de dias da prorrogação da experiência",
            drawer: "admission",
          });
        }
      }

      setRequired(isRequired);
      if (isRequired.length) {
        setEmpty(true);
      }
    };

    validateDraft();

    return () => {
      isActive = false;
    };
  }, [state, data.draft]);

  const focusFormField = (name: string) => {
    const fieldName = focusFieldByName[name] ?? name;
    const el = document.querySelector(
      `[name="${fieldName}"]`,
    ) as HTMLElement | null;
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (typeof (el as HTMLInputElement).focus === "function") {
      (el as HTMLInputElement).focus();
    }
  };

  const handleEmptyFieldRedirect = (field: any) => {
    if (!field.drawer) return;

    if (field.drawer === "basic") {
      focusFormField(field.name);
      return;
    }

    const drawer: any = drawersRef.filter(
      (row: any) => row.name === field.drawer,
    );

    if (drawer.length && drawer[0].ref?.current) {
      drawer[0].ref.current.click();
      window.setTimeout(() => focusFormField(field.name), 400);
    }
  };

  const EmptyAlert = () => {
    return (
      <Alert
        severity="error"
        sx={{
          flexDirection: "column",
          alignItems: "center",
          textAlign: "cenrer",
          mb: 3,
        }}
      >
        <Typography textAlign="center" variant="body2">
          Você possui campos a serem preenchidos antes do envio, confira abaixo
          os campos.
        </Typography>
      </Alert>
    );
  };

  const AllGoodAlert = () => {
    return (
      <Alert
        severity="info"
        sx={{
          flexDirection: "column",
          alignItems: "center",
          textAlign: "cenrer",
          mb: 3,
        }}
        icon={<CelebrationIcon />}
      >
        <Typography textAlign="center" variant="h5" fontWeight={700}>
          Muito bem!
        </Typography>
        <Typography textAlign="center" variant="body2">
          As informações importantes foram preenchidas, você pode prosseguir com
          o enviando clicando no botão abaixo.
        </Typography>
      </Alert>
    );
  };

  const handleSubmit = async () => {
    setState(false);
    setBackdrop(true);
    setError(false);

    try {
      await fetchData(
        `/hrm/fopag/${company.reference}/person/drafts/${data?._id}/dispatch`,
        {
          method: "GET",
        },
      );

      setData({ ...data, status: "processing" });
      setBackdrop(false);
      snackbar(
        "Nova admissão enviada para processamento com sucesso!",
        "success",
      );

      try {
        const feedbackState = await fetchData("/feedback/track", {
          method: "POST",
          body: JSON.stringify({ context: admissionContext }),
        });
        if (feedbackState?.shouldAsk) {
          setShowFeedbackModal(true);
        }
      } catch (e) {
        console.log(e);
      }
    } catch (error) {
      setError(true);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        color="warning"
        onClick={() => {
          setState(true);
        }}
        startIcon={<SendIcon />}
        disabled={data?.status !== "editing" || !data?._id}
      >
        Enviar
      </Button>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          ml: "0 !important",
        }}
        open={backdrop}
      >
        {!error && <CircularProgress color="inherit" />}
        {error && (
          <Card>
            <Stack spacing={2}>
              <Typography textAlign="center" variant="h4" color="primary.text">
                Ops!
              </Typography>
              <Typography
                textAlign="center"
                variant="body2"
                color="primary.text"
              >
                Não foi possível enviar o cadastro no momento.
                <br />
                Tente novamente mais tarde.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                size="small"
                color="primary"
                onClick={() => setBackdrop(false)}
              >
                Voltar
              </Button>
            </Stack>
          </Card>
        )}
      </Backdrop>
      <Drawer
        anchor="right"
        open={state}
        onClose={() => {
          setState(false);
        }}
      >
        <Box sx={{ p: 3, pt: 2, width: 400 }} width={300} height="100%">
          <Stack height="100%" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack
                direction="row"
                spacing={1}
                sx={{ flex: 1 }}
                alignItems="center"
              >
                <Typography
                  variant="body1"
                  sx={{ width: 170, wordWrap: "break-word" }}
                >
                  Confirme o envio
                </Typography>
              </Stack>
              <IconButton
                onClick={() => {
                  setState(false);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider sx={{ my: 2 }} />

            <Stack
              direction="row"
              spacing={2}
              position="fixed"
              right={0}
              bottom={0}
              zIndex={1}
              padding={2}
              sx={{
                width: 400,
                backgroundColor: "white",
                boxShadow: 5,
              }}
            >
              <Button
                fullWidth
                variant="outlined"
                size="small"
                color="secondary"
                onClick={() => setState(false)}
              >
                Cancelar
              </Button>
              <Button
                fullWidth
                variant="contained"
                size="small"
                color="warning"
                disabled={empty}
                startIcon={<SendIcon />}
                onClick={handleSubmit}
              >
                Enviar
              </Button>
            </Stack>

            <Box sx={{ pb: 10 }}>
              {empty && <EmptyAlert />}
              {!empty && (
                <>
                  <Card sx={{ border: "1px solid #CCC", p: 2, mb: 2 }}>
                    <Typography variant="body1" fontWeight={700}>
                      {`${data?.draft?.nome || "Nome não definido"}`}
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {company.name}
                    </Typography>
                  </Card>
                  <AllGoodAlert />
                </>
              )}
              {required.length > 0 &&
                required.map((row: any, index: number) => (
                  <Box key={index}>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="space-between"
                      sx={{
                        borderBottom: "1px solid #1f252c1f",
                        p: 1,
                      }}
                    >
                      <Link
                        component="button"
                        color="#FF0000"
                        onClick={() => {
                          setState(false);
                          window.setTimeout(
                            () => handleEmptyFieldRedirect(row),
                            300,
                          );
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="#FF0000"
                          fontWeight={500}
                        >
                          {row.label}
                        </Typography>
                      </Link>
                    </Stack>
                  </Box>
                ))}
            </Box>
          </Stack>
        </Box>
      </Drawer>

      <MuiModal
        open={showFeedbackModal}
        onClose={() => {
          if (feedbackSubmitting) return;
          setShowFeedbackModal(false);
        }}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" },
        }}
      >
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: 700 },
            maxWidth: 700,
            maxHeight: { xs: "90vh", md: "85vh" },
            p: 0,
            zIndex: 1301,
            overflow: "auto",
          }}
        >
          <CardHeader
            sx={{ borderBottom: "1px solid #CCCC" }}
            title={
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="h6" component="span" fontWeight={600}>
                  Avaliação de funcionalidade
                </Typography>
                <IconButton
                  aria-label="fechar"
                  size="small"
                  onClick={() => {
                    if (feedbackSubmitting) return;
                    setShowFeedbackModal(false);
                  }}
                  edge="end"
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            }
          />
          <CardContent sx={{ p: 2 }}>
            <FeatureFeedback
              submitting={feedbackSubmitting}
              onSubmit={async ({ rating, feedback }) => {
                setFeedbackSubmitting(true);
                try {
                  await fetchData("/feedback/ces", {
                    method: "POST",
                    body: JSON.stringify({
                      context: admissionContext,
                      rating,
                      comments: feedback || null,
                      clientId: company._id,
                      clientReference: company.reference,
                    }),
                  });
                } catch (e) {
                  console.log(e);
                }
                setFeedbackSubmitting(false);
              }}
              onSkip={async () => {
                setFeedbackSubmitting(true);
                try {
                  await fetchData("/feedback/skip", {
                    method: "POST",
                    body: JSON.stringify({
                      context: admissionContext,
                    }),
                  });
                } catch (e) {
                  console.log(e);
                }
                setFeedbackSubmitting(false);
                setShowFeedbackModal(false);
              }}
              onClose={() => {
                setShowFeedbackModal(false);
              }}
            />
          </CardContent>
        </Card>
      </MuiModal>
    </>
  );
};

export default HRManagementEmployeeExtract;
